import { css } from "@emotion/css";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { StatItem } from "@/uiKit/atoms/StatItem.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { AgpProfile } from "@/uiKit/molecules/graphs/AgpProfile.tsx";
import { GlycemiaBars } from "@/uiKit/molecules/graphs/GlycemiaBars.tsx";

import { compareValues } from "./Common";
import { GlycemiaStatsCGMNoData } from "./GlycemiaStatsCGMNoData";

type GlycemiaStatsProps = {
  patientId: string;
  from: DateTime;
  to: DateTime;
};

export const GlycemiaStatsCGM = ({ patientId, from, to }: GlycemiaStatsProps) => {
  const { t } = useTranslation();
  const { data: glycemiaStats } = Queries.diabetes.useGlycemiaIndicators({
    patientId,
    from: from,
    to: to,
    glycemiaMeasurementMethod: "cgm",
  });
  const diabetesDataDisplayMode = Queries.practitioner.useDiabetesDisplayMode(patientId);

  const isDexcom = diabetesDataDisplayMode.glycemiaDeviceName === "dexcom";

  const sizeOfRange = to.diff(from, "days").days;
  const { data: previousGlycemiaStats } = Queries.diabetes.useGlycemiaIndicators({
    patientId,
    from: from.minus({ days: sizeOfRange + 1 }),
    to: from.minus({ days: 1 }),
    glycemiaMeasurementMethod: "cgm",
  });

  const styles = useStyles(makeStyles);

  const { data: glycemiaParameters } = Queries.diabetes.useParameters(patientId);

  if (!glycemiaStats || !glycemiaParameters) {
    return <GlycemiaStatsCGMNoData patientId={patientId} />;
  }

  return (
    <Card>
      <CardHeader
        title={
          <div>
            <Typography variant="h5">{t("glycemia.title")}</Typography>
            <Typography variant="body">{isDexcom && t("pages.addMeasurementFile.dexcom.dexcomData")}</Typography>
          </div>
        }
      />
      <CardContent className={styles.content}>
        <div className={styles.row}>
          <div className={styles.statsCol}>
            <div className={styles.statsItems}>
              <StatItem
                className={styles.item}
                title={t("pages.patientMonitoring.glycemia.averageSensor")}
                value={glycemiaStats.meanGlycemia}
                unit={"mg/dL"}
                precision={0}
                variation={compareValues(glycemiaStats.meanGlycemia, previousGlycemiaStats?.meanGlycemia)}
                lowerIsBetter
              />
              <StatItem
                className={styles.item}
                title={t("pages.patientMonitoring.glycemia.estimatedHba1c")}
                value={glycemiaStats.hb1Ac ?? 0}
                unit={"%"}
                precision={1}
                variation={compareValues(glycemiaStats.hb1Ac, previousGlycemiaStats?.hb1Ac)}
                lowerIsBetter
              />
              <StatItem
                className={styles.item}
                title={t("pages.patientMonitoring.glycemia.coefVariation")}
                value={(glycemiaStats.coefficientOfVariation ?? 0) * 100}
                unit={"%"}
                precision={0}
                variation={
                  compareValues(glycemiaStats.coefficientOfVariation, previousGlycemiaStats?.coefficientOfVariation) *
                  100
                }
                lowerIsBetter
              />
              <StatItem
                className={styles.item}
                title={t("pages.patientMonitoring.glycemia.sensorUsage")}
                value={(glycemiaStats.usagePercentage ?? 0) * 100}
                unit={"%"}
                precision={0}
                variation={compareValues(glycemiaStats.usagePercentage, previousGlycemiaStats?.usagePercentage) * 100}
              />
              <StatItem
                className={styles.item}
                title={t("pages.patientMonitoring.glycemia.nbHypo")}
                value={glycemiaStats.numberOfHypoglycemia ?? 0}
                precision={0}
                variation={compareValues(
                  glycemiaStats.numberOfHypoglycemia,
                  previousGlycemiaStats?.numberOfHypoglycemia,
                )}
                lowerIsBetter
              />
            </div>
          </div>
          {glycemiaParameters.type === "CGM" && (
            <div>
              <Typography variant="body">{t("charts.timeBetweenThresholds")}</Typography>
              <GlycemiaBars
                width={300}
                height={275}
                data={glycemiaStats.glycemiaRanges}
                glycemiaThresholds={glycemiaParameters.parameters}
              />
            </div>
          )}
        </div>
        {glycemiaStats.agpProfile && glycemiaParameters.type === "CGM" && (
          <div className={styles.row}>
            <AgpProfile agp={glycemiaStats.agpProfile} glycemiaParameters={glycemiaParameters.parameters} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  content: css`
    display: flex;
    flex-direction: column;
  `,
  row: css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    row-gap: ${theme.spacing(8)};
  `,
  statsCol: css`
    flex: 1 1 50%;
  `,
  statsItems: css`
    display: flex;
    flex-direction: row;
    column-gap: ${theme.spacing(8)};
    row-gap: ${theme.spacing(12)};
    flex-wrap: wrap;
  `,
  item: css`
    flex: 0 1 30%;
  `,
  marginTop: css`
    margin-top: ${theme.spacing(8)};
  `,
});
